/**
 * Renders an assistant reply as HTML.
 *
 * Shared by ModernAI and Ask Modern so a bolded menu path looks the same in both.
 * Lifted verbatim out of MofoAI when the support assistant started showing raw
 * `**Settings > Filters**` to coaches (client 2026-09).
 *
 * Everything is escaped BEFORE any markup is applied, so a reply can never
 * inject HTML — the model's output is data, not markup.
 */

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const applyInlineMarkup = (value: string) =>
  value
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italics must open and close on a NON-underscore character, and open at
    // a word boundary. Support answers contain literal fill-in-the-blank
    // runs — 'Think: "IF ____, then ____"' — and the naive /_(.+?)_/ ate
    // them, rendering an italic comma (client 2026-09).
    .replace(/(^|[\s(["'])_([^_\s](?:[^_]*[^_\s])?)_(?=$|[\s.,!?:;)\]"'])/g, "$1<em>$2</em>");

export const formatMessageContent = (content: string): string => {
  const lines = content.split(/\r?\n/);
  const html: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let inCodeFence = false;

  const closeList = () => {
    if (listType) {
      html.push(listType === "ul" ? "</ul>" : "</ol>");
      listType = null;
    }
  };

  const closeCodeFence = () => {
    if (inCodeFence) {
      html.push("</code></pre>");
      inCodeFence = false;
    }
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (trimmed.startsWith("```")) {
      if (inCodeFence) {
        html.push("</code></pre>");
        inCodeFence = false;
      } else {
        closeList();
        const language = trimmed.slice(3).trim();
        html.push(
          `<pre class="my-3 overflow-x-auto rounded-lg bg-slate-900/90 p-4 text-xs text-slate-100"><code${
            language ? ` class="language-${escapeHtml(language)}"` : ""
          }>`
        );
        inCodeFence = true;
      }
      continue;
    }

    if (inCodeFence) {
      html.push(`${escapeHtml(rawLine)}\n`);
      continue;
    }

    if (!trimmed) {
      closeList();
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      closeList();
      const level = Math.min((headingMatch[1] ?? "").length + 3, 6);
      html.push(
        `<h${level} class="mt-4 text-sm font-semibold text-gray-900">${applyInlineMarkup(escapeHtml(headingMatch[2] ?? ""))}</h${level}>`
      );
      continue;
    }

    const unorderedMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (unorderedMatch) {
      if (listType !== "ul") {
        closeList();
        listType = "ul";
        html.push('<ul class="my-2 ml-4 list-disc space-y-1">');
      }
      html.push(`<li>${applyInlineMarkup(escapeHtml(unorderedMatch[1] ?? ""))}</li>`);
      continue;
    }

    const orderedMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);
    if (orderedMatch) {
      if (listType !== "ol") {
        closeList();
        listType = "ol";
        html.push('<ol class="my-2 ml-4 list-decimal space-y-1">');
      }
      html.push(`<li>${applyInlineMarkup(escapeHtml(orderedMatch[1] ?? ""))}</li>`);
      continue;
    }

    closeList();
    html.push(`<p class="my-2">${applyInlineMarkup(escapeHtml(trimmed))}</p>`);
  }

  closeList();
  closeCodeFence();
  return html.join("");
};
